import { getCurrencyByRegion, Money } from '@principle-theorem/accounting';
import {
  IIntegration,
  Integration,
  IntegrationCapability,
  IntegrationType,
} from '@principle-theorem/integrations';
import {
  BillingMeterId,
  IBillingMeter,
  IMarketingCampaignRun,
  IOrganisation,
  IOrganisationSubscription,
  IPractice,
  ISMSIntegrationData,
  IStripeAccountStatus,
  IStripeInvoice,
} from '@principle-theorem/principle-core/interfaces';
import {
  addDocAsWithRef,
  asReffable,
  asyncForEach,
  DocumentReference,
  Firestore,
  isSameRef,
  toFloat,
  toTimePeriod,
  toTimestampRange,
  WithRef,
} from '@principle-theorem/shared';
import { compact, difference, sum, uniq } from 'lodash';
import * as moment from 'moment-timezone';
import Stripe from 'stripe';
import {
  MarketingCampaignRun,
  StripeCreditGrantMetadata,
  StripeInvoiceMetadata,
} from '../marketing/campaign-run';
import { Organisation } from './organisation';
import { OrganisationSubscription } from './organisation-subscription';
import { StripeInvoice } from './stripe-invoice';

const METER_PAYLOAD_CUSTOMER_ID_KEY = 'stripe_customer_id';

export class OrganisationStripeAccount {
  static async trackMeteredEvent(
    stripe: Stripe,
    subscription: WithRef<IOrganisationSubscription>,
    practiceRef: DocumentReference<IPractice>,
    billingMeter: BillingMeterId,
    units: number,
    uniqueId: string,
    meteredBillingEnabled: boolean
  ): Promise<Stripe.Response<Stripe.Billing.MeterEvent> | undefined> {
    if (!meteredBillingEnabled) {
      return;
    }

    // eslint-disable-next-line no-console
    console.log('Tracking metered event:', {
      subscription: subscription.ref.path,
      practiceRef: practiceRef.path,
      billingMeter,
      units,
      uniqueId,
    });

    try {
      const response = await stripe.billing.meterEvents.create({
        event_name: billingMeter,
        identifier: uniqueId,
        payload: {
          [METER_PAYLOAD_CUSTOMER_ID_KEY]: subscription.customerId,
          value: `${units}`,
        },
      });
      return response;
    } catch (error) {
      if (error instanceof stripe.errors.StripeError) {
        // eslint-disable-next-line no-console
        console.warn(`Error tracking metered Event: ${error.message}`);
        return;
      }
      throw error;
    }
  }

  static async getBillingStatus(
    stripe: Stripe,
    subscription: WithRef<IOrganisationSubscription>
  ): Promise<IStripeAccountStatus> {
    if (!subscription.subscriptionId) {
      throw new Error('No subscriptionId on organisation subscription');
    }

    const upcomingInvoice = await stripe.invoices.createPreview({
      customer: subscription.customerId,
      subscription: subscription.subscriptionId,
    });

    const meterUsage = compact(
      await asyncForEach(upcomingInvoice.lines.data, async (line) => {
        const priceId =
          line.pricing?.type === 'price_details'
            ? line.pricing?.price_details?.price
            : undefined;

        if (!priceId) {
          return;
        }

        const price = await stripe.prices.retrieve(priceId);
        const stripeMeterId = price.recurring?.meter || undefined;
        const billingMeter =
          stripeMeterId &&
          subscription.billingMeters?.find(
            (meter) => meter.stripeMeterId === stripeMeterId
          );
        if (!billingMeter) {
          return;
        }

        const costPerUnit = Money.fromCents(
          price?.unit_amount_decimal ? toFloat(price.unit_amount_decimal) : 0,
          3
        );

        return {
          meterId: billingMeter.billingMeterId,
          practiceRef: billingMeter.practiceRef,
          cost: Money.fromCents(line.amount),
          units: line.quantity ?? 0,
          costPerUnit,
        };
      })
    );

    const creditBalanceSummary =
      await stripe.billing.creditBalanceSummary.retrieve({
        customer: subscription.customerId,
        filter: {
          type: 'applicability_scope',
          applicability_scope: {
            price_type: 'metered',
          },
        },
      });

    const periodStart = moment.unix(upcomingInvoice.period_start);
    const periodEnd = moment.unix(upcomingInvoice.period_end);
    const invoiceSummary = creditBalanceSummary.balances.find(
      (balance) =>
        balance.available_balance.monetary?.currency ===
        upcomingInvoice.currency
    );
    const credit = Money.fromCents(
      invoiceSummary?.available_balance.monetary?.value ?? 0
    );

    const meterUsageTotal = sum(meterUsage.map((usage) => usage.cost ?? 0));
    const balance = Money.amount(meterUsageTotal - credit);

    const marketingUsageTotal = sum(
      meterUsage
        .filter(
          (usage) => usage.meterId === BillingMeterId.SMSMarketingSegments
        )
        .map((usage) => usage.cost ?? 0)
    );
    const marketingBalance = Money.amount(marketingUsageTotal - credit);

    return {
      period: toTimestampRange(toTimePeriod(periodStart, periodEnd)),
      meterUsage: compact(meterUsage),
      meterUsageTotal,
      marketingUsageTotal,
      credit,
      balance,
      marketingBalance,
    };
  }

  static async createDepositInvoice(
    stripe: Stripe,
    organisation: WithRef<IOrganisation>,
    subscription: WithRef<IOrganisationSubscription>,
    amount: number,
    campaignRun?: WithRef<IMarketingCampaignRun>
  ): Promise<WithRef<IStripeInvoice>> {
    const metadata: StripeInvoiceMetadata = {};
    if (campaignRun) {
      metadata.marketingCampaignRef =
        MarketingCampaignRun.campaignRef(campaignRun).path;
      metadata.marketingCampaignRunRef = campaignRun.ref.path;
    }

    const currency = getCurrencyByRegion(organisation.region).toLowerCase();

    const invoice = await stripe.invoices.create({
      customer: subscription.customerId,
      currency,
      automatic_tax: { enabled: false },
      collection_method: 'charge_automatically',
      pending_invoice_items_behavior: 'exclude',
      metadata,
    });

    await stripe.invoiceItems.create({
      customer: subscription.customerId,
      amount: Money.toCents(amount),
      currency,
      description: `Principle SMS Credits`,
      invoice: invoice.id,
      metadata,
    });

    if (!invoice.id) {
      throw new Error(
        `Failed to create invoice for customer: ${subscription.customerId}`
      );
    }

    const finalized = await stripe.invoices.finalizeInvoice(invoice.id, {
      auto_advance: true,
    });

    return addDocAsWithRef(
      OrganisationSubscription.stripeInvoiceCol(subscription),
      StripeInvoice.init({
        invoice: StripeInvoice.fromStripeData(finalized),
        campaignRunRef: campaignRun?.ref,
      })
    );
  }

  static async cancelDepositInvoice(
    stripe: Stripe,
    invoice: WithRef<IStripeInvoice>
  ): Promise<void> {
    try {
      const response = await stripe.invoices.voidInvoice(invoice.invoice.id);
      await Firestore.patchDoc(invoice.ref, {
        invoice: StripeInvoice.fromStripeData(response),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      // eslint-disable-next-line no-console
      console.error(`Error cancelling invoice: ${message}`);
      return;
    }
  }

  static async syncBillingMeters(
    stripe: Stripe,
    subscription: WithRef<IOrganisationSubscription>,
    meterIds: BillingMeterId[]
  ): Promise<IBillingMeter[]> {
    const practices =
      await OrganisationSubscription.getSubscriptionPractices(subscription);
    // eslint-disable-next-line no-console
    console.log(
      `Syncing billing meters for subscription ${
        subscription.ref.id
      } - practices: ${practices.length}, meterIds: ${meterIds.join(', ')}`
    );

    const organisationRef = Firestore.getParentDocRef<IOrganisation>(
      subscription.ref
    );
    const organisation = await Firestore.getDoc(organisationRef);
    const smsIntegrations =
      await Integration.getByCapability<ISMSIntegrationData>(
        Organisation.integrationCol(organisation),
        IntegrationCapability.SMS
      );
    const smsProvider =
      organisation.integrations?.smsProvider ?? IntegrationType.Twilio;
    const integration = smsIntegrations.find(
      (smsIntegration) => smsIntegration.type === smsProvider
    );
    if (!integration) {
      throw new Error(
        `No SMS integration found for organisation ${organisation.ref.id}`
      );
    }

    const practiceBillingMeters = await asyncForEach(practices, (practice) =>
      asyncForEach(meterIds, (meterId) =>
        this.syncBillingMeter(
          stripe,
          subscription,
          practice,
          meterId,
          integration
        )
      )
    );
    const billingMeters = practiceBillingMeters.flat();
    await this.upsertMeterPricesToSubscription(
      stripe,
      subscription,
      billingMeters
    );
    return billingMeters;
  }

  static async syncBillingMeter(
    stripe: Stripe,
    subscription: WithRef<IOrganisationSubscription>,
    practice: WithRef<IPractice>,
    meterId: BillingMeterId,
    integration: IIntegration<ISMSIntegrationData>
  ): Promise<IBillingMeter> {
    return this.resolveBillingMeter(
      stripe,
      subscription,
      practice,
      meterId,
      integration
    );
  }

  static async billingMeterLookup(
    stripe: Stripe,
    meterId: BillingMeterId
  ): Promise<Stripe.Billing.Meter | undefined> {
    const eventName = meterId;
    const stripeMeters = await stripe.billing.meters.list();
    // eslint-disable-next-line no-console
    console.log(
      `Looking up Stripe Meter for event name: ${eventName}, found ${stripeMeters.data.length} meters`
    );

    return stripeMeters.data.find(
      (stripeMeter) => stripeMeter.event_name === eventName
    );
  }

  static findBillingMeter(
    subscription: IOrganisationSubscription,
    practiceRef: DocumentReference<IPractice>,
    meterId: BillingMeterId
  ): IBillingMeter | undefined {
    return subscription.billingMeters?.find(
      (billingMeter) =>
        isSameRef(billingMeter.practiceRef, practiceRef) &&
        billingMeter.billingMeterId === meterId
    );
  }

  static async resolveBillingMeter(
    stripe: Stripe,
    subscription: WithRef<IOrganisationSubscription>,
    practice: WithRef<IPractice>,
    meterId: BillingMeterId,
    integration: IIntegration<ISMSIntegrationData>
  ): Promise<IBillingMeter> {
    const priceLookupId = this._getMeteredPricingId(meterId, integration);
    if (!priceLookupId) {
      throw new Error(`No pricing ID found for meter ID: ${meterId}`);
    }

    const prices = await stripe.prices.list({
      lookup_keys: [priceLookupId],
      limit: 1,
    });
    const stripePrice = prices.data[0];
    if (!stripePrice) {
      throw new Error(`No Stripe Price found for lookup ID: ${priceLookupId}`);
    }

    const existing = this.findBillingMeter(subscription, practice.ref, meterId);
    if (existing) {
      // eslint-disable-next-line no-console
      console.log(
        `Found existing billing meter for practice ${practice.ref.id} - ${meterId}`,
        existing
      );
      const existingStripeMeter = await stripe.billing.meters.retrieve(
        existing.stripeMeterId
      );
      if (existingStripeMeter) {
        // eslint-disable-next-line no-console
        console.log(
          `Existing Stripe Meter verified for practice ${practice.ref.id} - ${meterId}`
        );
        return existing;
      }
    }

    const foundMeter = await this.billingMeterLookup(stripe, meterId);
    if (!foundMeter) {
      throw new Error(
        `No existing Stripe Meter found for event name: ${meterId}`
      );
    }

    return {
      billingMeterId: meterId,
      practiceRef: practice.ref,
      stripeMeterId: foundMeter.id,
      stripePriceId: stripePrice.id,
    };
  }

  static async upsertMeterProduct(
    stripe: Stripe,
    stripeProductId: string
  ): Promise<void> {
    const existingProduct = await stripe.products.retrieve(stripeProductId);
    if (!existingProduct.active) {
      await stripe.products.update(stripeProductId, {
        active: true,
      });
    }
  }

  static isSameOrganisationSubscription(
    aOrgSub: IOrganisationSubscription,
    bOrgSub: IOrganisationSubscription
  ): boolean {
    return (
      aOrgSub.customerId === bOrgSub.customerId &&
      aOrgSub.subscriptionId === bOrgSub.subscriptionId
    );
  }

  static async upsertMeterPricesToSubscription(
    stripe: Stripe,
    subsciption: IOrganisationSubscription,
    billingMeters: IBillingMeter[]
  ): Promise<void> {
    // eslint-disable-next-line no-console
    console.log(
      `Upserting meter prices to subscription ${subsciption.subscriptionId} - ${billingMeters.length} meters`
    );
    const priceIds = billingMeters.map(
      (billingMeter) => billingMeter.stripePriceId
    );

    const stripeSub = await stripe.subscriptions.retrieve(
      subsciption.subscriptionId
    );

    // eslint-disable-next-line no-console
    console.log(`Current subscription items: ${stripeSub.items.data.length}`);
    const existingMeterSubItems = stripeSub.items.data.filter(
      (subItem) =>
        subItem.price.recurring &&
        subItem.price.recurring.meter &&
        subItem.price.recurring.usage_type === 'metered'
    );
    // eslint-disable-next-line no-console
    console.log(
      `Existing metered subscription items: ${existingMeterSubItems.length}`
    );

    const existingPriceIds = existingMeterSubItems.map(
      (subItem) => subItem.price.id
    );
    // eslint-disable-next-line no-console
    console.log(`Existing metered price IDs: ${existingPriceIds.join(', ')}`);

    const priceIdsToAdd = uniq(difference(priceIds, existingPriceIds));
    // eslint-disable-next-line no-console
    console.log(`Price IDs to add: ${priceIdsToAdd.join(', ')}`);

    await stripe.subscriptions.update(subsciption.subscriptionId, {
      items: priceIdsToAdd.map((price) => ({ price })),
    });

    const priceIdsToRemove = difference(existingPriceIds, priceIds);
    // eslint-disable-next-line no-console
    console.log(`Price IDs to remove: ${priceIdsToRemove.join(', ')}`);

    const subItemsToRemove = existingMeterSubItems
      .filter((subItem) => priceIdsToRemove.includes(subItem.price.id))
      .map((subItem) => subItem.id);
    // eslint-disable-next-line no-console
    console.log(
      `Subscription item IDs to remove: ${subItemsToRemove.join(', ')}`
    );

    await asyncForEach(subItemsToRemove, (subItemId) =>
      stripe.subscriptionItems.del(subItemId)
    );
  }

  static async issueCreditGrantForStripeInvoice(
    stripe: Stripe,
    organisation: WithRef<IOrganisation>,
    subscription: IOrganisationSubscription,
    invoice: WithRef<IStripeInvoice>,
    amount: number,
    idempotencyKey?: string
  ): Promise<string | undefined> {
    const creditGrantName = `Principle SMS Credits - (${invoice.invoice.number})`;

    const metadata: StripeCreditGrantMetadata = {
      fromInvoiceId: invoice.invoice.id,
      fromStripeInvoiceId: invoice.ref.path,
    };
    if (invoice.campaignRunRef) {
      const campaignRef = MarketingCampaignRun.campaignRef(
        asReffable(invoice.campaignRunRef)
      );
      metadata.marketingCampaignRef = campaignRef.path;
      metadata.marketingCampaignRunRef = invoice.campaignRunRef.path;
    }

    const currency = getCurrencyByRegion(organisation.region).toLowerCase();

    const creditGrant = await stripe.billing.creditGrants.create(
      {
        customer: subscription.customerId,
        name: creditGrantName,
        applicability_config: {
          scope: {
            price_type: 'metered',
          },
        },
        category: 'paid',
        amount: {
          type: 'monetary',
          monetary: {
            value: Money.toCents(amount),
            currency,
          },
        },
        metadata,
      },
      // The webhook passes the Stripe event id, so a redelivery that re-runs
      // the handler resolves to the same grant instead of issuing a second one.
      idempotencyKey ? { idempotencyKey } : undefined
    );

    return creditGrant.id;
  }

  private static _getMeteredPricingId(
    meterId: BillingMeterId,
    integration: IIntegration<ISMSIntegrationData>
  ): string | undefined {
    switch (meterId) {
      case BillingMeterId.SMSOutboundSegments:
        return integration.data.outboundPriceId;
      case BillingMeterId.SMSInboundSegments:
        return integration.data.inboundPriceId;
      case BillingMeterId.SMSMarketingSegments:
        return integration.data.marketingPriceId;
      default:
        return undefined;
    }
  }
}
